import type { ClientApi } from '@openclassrooms/sdk-web';
import type { ChapterContentResponse, EmbeddedChapter, ChapterMetadataResponse } from '../types';
import { getEmbeddedChapterKey } from './expandableEmbeddedChapter';

/**
 * Type guard to determine if a value is a lazy request object that needs execution,
 * rather than a direct response. Returns true if the value has an `execute` function,
 * indicating it's a lazy/deferred request that must be awaited.
 */
type ExecutableMetadataRequest = { execute: (...args: never[]) => Promise<object> };

const isExecutableRequest = (value: object | null): value is ExecutableMetadataRequest =>
  value !== null &&
  typeof value === 'object' &&
  'execute' in value &&
  typeof value.execute === 'function';

const normalizeChapterMetadata = (value: object): ChapterMetadataResponse | null => {
  const title = 'title' in value && typeof value.title === 'string' ? value.title : undefined;
  const userHasCompletedChapter =
    'userHasCompletedChapter' in value && typeof value.userHasCompletedChapter === 'boolean'
      ? value.userHasCompletedChapter
      : undefined;

  if (title === undefined && userHasCompletedChapter === undefined) {
    return null;
  }

  return {
    title,
    userHasCompletedChapter,
  };
};

interface ChapterDataFetcherResult {
  content: ChapterContentResponse;
  metadata: ChapterMetadataResponse | null;
  previousEmbeddedChapters: Array<{ chapterKey: string; completion: boolean }>;
}

export const fetchChapterData = async (
  client: ClientApi,
  courseId: number,
  chapterId: number,
  previousEmbeddedChapters: EmbeddedChapter[]
): Promise<ChapterDataFetcherResult> => {
  const chaptersHelper = client.helpers.learningContent.chapters;
  const userMe = client.helpers.user?.me;

  const chapterContentPromise: Promise<ChapterContentResponse> = Promise.resolve(
    chaptersHelper.getContent(courseId, chapterId)
  );

  const canFetchUserChapterCompletion =
    Boolean(userMe) && typeof client.createGetRequest === 'function';

  /**
   * Two different APIs are used depending on authentication context:
   * 1. Authenticated API (createGetRequest) - Returns user-specific completion status
   * 2. Public API (chaptersHelper) - Returns public chapter metadata (fallback for anonymous users)
   *
   * We need both because the authenticated endpoint reveals whether the current user
   * has completed a chapter, while the public API only returns generic chapter info.
   */

  /**
   * Fetch user-specific chapter metadata via authenticated API endpoint.
   * This endpoint requires a user context and returns completion status for the authenticated user.
   * Used when: user is logged in and we can get their userId.
   */
  const fetchChapterMetadataViaApi = (
    userId: number,
    targetCourseId: number,
    targetChapterId: number
  ): Promise<ChapterMetadataResponse | null> =>
    client
      .createGetRequest('/users/:userId/courses/:courseId/chapters/:chapterId')
      .setRouteParams({ userId, courseId: targetCourseId, chapterId: targetChapterId })
      .execute()
      .then(({ body }) =>
        body && typeof body === 'object' ? normalizeChapterMetadata(body) : null
      );

  /**
   * Fetch public chapter metadata via the chaptersHelper API.
   * Does NOT require user authentication. Returns public chapter data.
   * Used when: user is not logged in or userMe/createGetRequest are unavailable.
   * Handles both lazy (executable) and eager response patterns from the SDK.
   */
  const fetchChapterMetadataViaHelper = (
    targetCourseId: number,
    targetChapterId: number
  ): Promise<ChapterMetadataResponse | null> =>
    Promise.resolve(chaptersHelper.get(targetCourseId, targetChapterId)).then(
      (responseOrRequest) => {
        if (isExecutableRequest(responseOrRequest)) {
          return responseOrRequest.execute().then((response) => {
            if ('body' in response) {
              const { body } = response;
              return body && typeof body === 'object' ? normalizeChapterMetadata(body) : null;
            }

            return null;
          });
        }

        if ('body' in responseOrRequest) {
          const { body } = responseOrRequest;
          return body && typeof body === 'object' ? normalizeChapterMetadata(body) : null;
        }

        return null;
      }
    );

  /**
   * Fetch metadata for the current chapter and (optionally) all previous embedded chapters.
   * Returns results indexed by chapter key for easy lookup.
   */
  const fetchAllChapterMetadata = async (): Promise<
    Record<string, ChapterMetadataResponse | null>
  > => {
    const currentChapterKey = `${courseId}:${chapterId}`;

    // Optimization: If no previous chapters, skip batch fetching and just get current chapter
    if (previousEmbeddedChapters.length === 0) {
      const metadata = await (canFetchUserChapterCompletion
        ? userMe!
            .getUserId()
            .then((userId) => fetchChapterMetadataViaApi(userId, courseId, chapterId))
        : fetchChapterMetadataViaHelper(courseId, chapterId));

      return { [currentChapterKey]: metadata };
    }

    // Batch fetch: combine current chapter with previous chapters for parallel execution
    const allChaptersToFetch: Array<{ chapter: EmbeddedChapter; key: string; isCurrent: boolean }> =
      [
        { chapter: { courseId, chapterId }, key: currentChapterKey, isCurrent: true },
        ...previousEmbeddedChapters.map((chapter) => ({
          chapter,
          key: getEmbeddedChapterKey(chapter),
          isCurrent: false,
        })),
      ];

    if (canFetchUserChapterCompletion) {
      const userId = await userMe!.getUserId();

      const results = await Promise.allSettled(
        allChaptersToFetch.map(({ chapter, key }) =>
          fetchChapterMetadataViaApi(userId, chapter.courseId, chapter.chapterId).then(
            (metadata) => ({ key, metadata })
          )
        )
      );

      return Object.fromEntries(
        results.map((result, i) =>
          result.status === 'fulfilled'
            ? [result.value.key, result.value.metadata]
            : [allChaptersToFetch[i].key, null]
        )
      );
    }

    const results = await Promise.allSettled(
      allChaptersToFetch.map(({ chapter, key }) =>
        fetchChapterMetadataViaHelper(chapter.courseId, chapter.chapterId).then((metadata) => ({
          key,
          metadata,
        }))
      )
    );

    return Object.fromEntries(
      results.map((result, i) =>
        result.status === 'fulfilled'
          ? [result.value.key, result.value.metadata]
          : [allChaptersToFetch[i].key, null]
      )
    );
  };

  const metadataPromise = fetchAllChapterMetadata().then((allMetadata) => {
    // Extract current chapter metadata
    const currentChapterKey = `${courseId}:${chapterId}`;
    const currentMetadata = allMetadata[currentChapterKey] ?? null;

    // Extract previous embedded chapters completion status
    const previousCompletions = previousEmbeddedChapters.map((embeddedChapter) => {
      const key = getEmbeddedChapterKey(embeddedChapter);
      const metadata = allMetadata[key];
      const completion = metadata?.userHasCompletedChapter ?? false;

      return { chapterKey: key, completion };
    });

    return { currentMetadata, previousCompletions };
  });

  const results = await Promise.allSettled([chapterContentPromise, metadataPromise]);

  const [contentResult, metadataResult] = results;

  return {
    content: contentResult.status === 'fulfilled' ? contentResult.value : '',
    metadata: metadataResult.status === 'fulfilled' ? metadataResult.value.currentMetadata : null,
    previousEmbeddedChapters:
      metadataResult.status === 'fulfilled' ? metadataResult.value.previousCompletions : [],
  };
};
